import * as pdfjsLib from "pdfjs-dist";

// Ensure worker is configured (same as usePdfLoader)
pdfjsLib.GlobalWorkerOptions.workerSrc =
  "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";

export interface ExtractedPage {
  pageNumber: number;
  text: string;
  wordCount: number;
}

export interface ExtractionProgress {
  currentPage: number;
  totalPages: number;
  percentage: number;
}

export interface ExtractionResult {
  pages: ExtractedPage[];
  totalPages: number;
  totalWords: number;
  success: boolean;
  error?: string;
}

/**
 * Extract text from a PDF file using PDF.js
 * 
 * @param pdfUrl - URL to the PDF file (can be blob URL or remote URL)
 * @param onProgress - Optional callback for progress updates
 * @returns Promise with extraction result
 */
export async function extractTextFromPdf(
  pdfUrl: string,
  onProgress?: (progress: ExtractionProgress) => void
): Promise<ExtractionResult> {
  try {
    // Load the PDF document
    const loadingTask = pdfjsLib.getDocument(pdfUrl);
    const pdfDoc = await loadingTask.promise;
    const totalPages = pdfDoc.numPages;

    console.log(`[extractTextFromPdf] Starting extraction for ${totalPages} pages`);

    const pages: ExtractedPage[] = [];
    let totalWords = 0;

    // Extract text from each page
    for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
      const page = await pdfDoc.getPage(pageNum);
      const textContent = await page.getTextContent();

      // Combine text items into a single string
      const pageText = textContent.items
        .map((item) => {
          if ("str" in item) {
            return item.str;
          }
          return "";
        })
        .join(" ")
        .trim();

      // Count words
      const wordCount = pageText.split(/\s+/).filter(Boolean).length;
      totalWords += wordCount;

      pages.push({
        pageNumber: pageNum,
        text: pageText,
        wordCount,
      });

      // Report progress
      if (onProgress) {
        onProgress({
          currentPage: pageNum,
          totalPages,
          percentage: Math.round((pageNum / totalPages) * 100),
        });
      }
    }

    console.log(`[extractTextFromPdf] Extracted ${totalWords} words from ${totalPages} pages`);

    return {
      pages,
      totalPages,
      totalWords,
      success: true,
    };
  } catch (error) {
    console.error("[extractTextFromPdf] Error:", error);
    return {
      pages: [],
      totalPages: 0,
      totalWords: 0,
      success: false,
      error: error instanceof Error ? error.message : "Failed to extract text from PDF",
    };
  }
}

/**
 * Extract text from a PDF File object
 */
export async function extractTextFromPdfFile(
  file: File,
  onProgress?: (progress: ExtractionProgress) => void
): Promise<ExtractionResult> {
  const blobUrl = URL.createObjectURL(file);
  try {
    return await extractTextFromPdf(blobUrl, onProgress);
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
}

/**
 * Get full document text as a single string (for AI processing)
 */
export function getFullDocumentText(pages: ExtractedPage[]): string {
  return pages
    .map((p) => `[Page ${p.pageNumber}]\n${p.text}`)
    .join("\n\n");
}

/**
 * Get text for a specific page range
 */
export function getPageRangeText(
  pages: ExtractedPage[],
  startPage: number,
  endPage: number
): string {
  return pages
    .filter((p) => p.pageNumber >= startPage && p.pageNumber <= endPage)
    .map((p) => `[Page ${p.pageNumber}]\n${p.text}`)
    .join("\n\n");
}